import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL_WITH_API } from '../config/api';
import AdminHeader from '../components/AdminHeader';
import ImpersonationBanner from '../components/ImpersonationBanner';
import SideMenuCard, { SideMenuItem } from '../components/SideMenuCard';
import { isStagingOrLocalhost } from '../utils/environment';
import { getExtensionId } from '../config/extension';
import { getChromeRuntime } from '../utils/extensionMessaging';
import { clearAllAuthStorage, getEffectiveToken, getEffectiveUser, isImpersonating } from '../utils/authSession';
import { MENU_ICON_PATHS } from '../components/menuIcons';
import { getGpsBadgeStyles, getMaxRecordsBadgeStyles, getOnOffBadgeStyles } from '../utils/adminBadgeStyles';

interface DomainRow {
  domain: string;
  teacherCount: number;
  adminCount: number;
  groupCount?: number;
  totalWeeklyClicks?: number;
  avgWeeklyClicks?: number;
  totalGpsClicks?: number;
  avgGpsClicks?: number;
  totalGpsInserts?: number;
  avgGpsInserts?: number;
  lastClickAt?: string | null;
  coTeacherAccessLevel?: 'off' | 'basic' | 'premium';
  dashboardMaxRecords?: number;
  feedbackRequestEmailsEnabled?: boolean;
  naOptionAccessEnabled?: boolean;
  selectionReminderAccessEnabled?: boolean;
}

type SortColumn =
  | 'domain'
  | 'teacherCount'
  | 'adminCount'
  | 'adminPortal'
  | 'coTeacherAccessLevel'
  | 'dashboardMaxRecords'
  | 'feedbackRequestEmailsEnabled'
  | 'naOptionAccessEnabled'
  | 'selectionReminderAccessEnabled'
  | 'groupCount'
  | 'totalWeeklyClicks'
  | 'avgWeeklyClicks'
  | 'totalGpsClicks'
  | 'avgGpsClicks'
  | 'totalGpsInserts'
  | 'avgGpsInserts'
  | 'lastClickAt';
type SortDirection = 'asc' | 'desc';

const MAX_RECORD_CHOICES = [
  { value: 1000, label: 'Free — 1,000' },
  { value: 25000, label: 'Paid — 25,000' },
  { value: 50000, label: 'XL — 50,000' }
] as const;
const LEGACY_MAX_RECORD_VALUES = [5000, 10000] as const;
const KNOWN_MAX_RECORD_VALUES = [1000, 5000, 10000, 25000, 50000] as const;
const CO_TEACHER_LEVELS = ['off', 'basic', 'premium'] as const;

const compactHeaderStyle: React.CSSProperties = {
  padding: '8px 6px',
  fontWeight: 600,
  color: 'white',
  borderRight: '1px solid rgba(255,255,255,0.3)',
  cursor: 'pointer',
  userSelect: 'none',
  whiteSpace: 'normal',
  lineHeight: 1.15,
  fontSize: '12px',
  verticalAlign: 'bottom'
};

const compactCellStyle: React.CSSProperties = {
  padding: '8px 6px',
  borderRight: '1px solid #eee',
  textAlign: 'center',
  whiteSpace: 'nowrap'
};

const AdminDomainsPage = () => {
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const usePersistentSidebar = isStagingOrLocalhost();
  const [viewerIsSuperAdmin, setViewerIsSuperAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [domains, setDomains] = useState<DomainRow[]>([]);
  const [sortColumn, setSortColumn] = useState<SortColumn>('domain');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [coTeacherOverrides, setCoTeacherOverrides] = useState<Record<string, 'off' | 'basic' | 'premium'>>({});
  const [maxRecordOverrides, setMaxRecordOverrides] = useState<Record<string, number>>({});
  const [feedbackEmailOverrides, setFeedbackEmailOverrides] = useState<Record<string, boolean>>({});
  const [naOptionOverrides, setNaOptionOverrides] = useState<Record<string, boolean>>({});
  const [reminderOverrides, setReminderOverrides] = useState<Record<string, boolean>>({});
  const [saving, setSaving] = useState(false);
  const [selectedDomainFilter, setSelectedDomainFilter] = useState('');

  useEffect(() => {
    document.title = 'Domains - Traffic Lite Admin';
    try {
      const user = getEffectiveUser();
      if (!user) {
        navigate('/signin');
        return;
      }
      const isSuper = Boolean(user?.isSuperAdmin) && !isImpersonating();
      setViewerIsSuperAdmin(isSuper);
      if (!isSuper) {
        navigate('/admin');
        return;
      }
      void fetchDomains();
    } catch (parseError) {
      console.warn('Could not parse trafficLiteUser from localStorage:', parseError);
      navigate('/signin');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchDomains = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = getEffectiveToken();
      if (!token) {
        setError('Not authenticated. Please sign in again.');
        setLoading(false);
        return;
      }

      const baseUrl = API_BASE_URL_WITH_API.endsWith('/api')
        ? API_BASE_URL_WITH_API
        : `${API_BASE_URL_WITH_API}/api`;
      const response = await fetch(`${baseUrl}/admin/domains`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        cache: 'no-store'
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || `Failed to fetch domains (${response.status})`);
      }

      const data = await response.json();
      setDomains(Array.isArray(data?.domains) ? data.domains : []);
      setCoTeacherOverrides({});
      setMaxRecordOverrides({});
      setFeedbackEmailOverrides({});
      setNaOptionOverrides({});
      setReminderOverrides({});
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to fetch domains';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (column: SortColumn) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
      return;
    }
    setSortColumn(column);
    setSortDirection('asc');
  };

  const renderSortIndicator = (column: SortColumn) => {
    if (sortColumn !== column) return null;
    return (
      <span className="traffic-sort-indicator" style={{ marginLeft: '4px', display: 'inline-flex', alignItems: 'center' }}>
        <img
          src={sortDirection === 'asc' ? MENU_ICON_PATHS.upArrow : MENU_ICON_PATHS.downArrow}
          alt=""
          style={{ width: '11px', height: '11px', objectFit: 'contain' }}
        />
      </span>
    );
  };

  const domainFilterOptions = useMemo(
    () => [...domains].map((item) => item.domain).sort((a, b) => a.localeCompare(b)),
    [domains]
  );

  const getStoredMaxRecords = (domain: string) => {
    const existing = domains.find((item) => item.domain === domain);
    const configured = Number(existing?.dashboardMaxRecords);
    return (KNOWN_MAX_RECORD_VALUES as readonly number[]).includes(configured) ? configured : 1000;
  };

  const getEffectiveMaxRecords = (domain: string) => {
    if (maxRecordOverrides[domain] !== undefined) return maxRecordOverrides[domain];
    return getStoredMaxRecords(domain);
  };

  const maxRecordChoicesFor = (domain: string) => {
    const stored = getStoredMaxRecords(domain);
    const choices: Array<{ value: number; label: string }> = MAX_RECORD_CHOICES.map((choice) => ({ ...choice }));
    if ((LEGACY_MAX_RECORD_VALUES as readonly number[]).includes(stored)) {
      choices.push({ value: stored, label: `${stored.toLocaleString()} (current)` });
    }
    return choices;
  };

  const filteredDomains = useMemo(() => {
    if (!selectedDomainFilter.trim()) return domains;
    return domains.filter((item) => item.domain === selectedDomainFilter);
  }, [domains, selectedDomainFilter]);

  const getEffectiveCoTeacherAccess = (domain: string): 'off' | 'basic' | 'premium' => {
    if (coTeacherOverrides[domain] !== undefined) return coTeacherOverrides[domain];
    const existing = domains.find((item) => item.domain === domain);
    const level = String(existing?.coTeacherAccessLevel || '').toLowerCase();
    return CO_TEACHER_LEVELS.includes(level as (typeof CO_TEACHER_LEVELS)[number])
      ? (level as 'off' | 'basic' | 'premium')
      : 'off';
  };

  const getEffectiveNaOptionAccess = (domain: string): boolean => {
    if (naOptionOverrides[domain] !== undefined) return naOptionOverrides[domain];
    const existing = domains.find((item) => item.domain === domain);
    return existing?.naOptionAccessEnabled === true;
  };

  const getEffectiveReminderAccess = (domain: string): boolean => {
    if (reminderOverrides[domain] !== undefined) return reminderOverrides[domain];
    const existing = domains.find((item) => item.domain === domain);
    return existing?.selectionReminderAccessEnabled === true;
  };

  const getEffectiveFeedbackEmailsEnabled = (domain: string): boolean => {
    if (feedbackEmailOverrides[domain] !== undefined) return feedbackEmailOverrides[domain];
    const existing = domains.find((item) => item.domain === domain);
    return existing?.feedbackRequestEmailsEnabled !== false;
  };

  const formatLastClick = (isoDate?: string | null) => {
    if (!isoDate) return '';
    const date = new Date(isoDate);
    if (Number.isNaN(date.getTime())) return '';
    return new Intl.DateTimeFormat(undefined, {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    }).format(date);
  };

  const formatAverage = (value?: number) => {
    const numeric = Number(value || 0);
    return Number.isInteger(numeric) ? String(numeric) : numeric.toFixed(1);
  };

  const sortedDomains = useMemo(() => {
    const items = [...filteredDomains];
    const numericValue = (item: DomainRow, column: SortColumn): number => {
      if (column === 'teacherCount') return Number(item.teacherCount || 0);
      if (column === 'adminCount') return Number(item.adminCount || 0);
      if (column === 'groupCount') return Number(item.groupCount || 0);
      if (column === 'totalWeeklyClicks') return Number(item.totalWeeklyClicks || 0);
      if (column === 'avgWeeklyClicks') return Number(item.avgWeeklyClicks || 0);
      if (column === 'totalGpsClicks') return Number(item.totalGpsClicks || 0);
      if (column === 'avgGpsClicks') return Number(item.avgGpsClicks || 0);
      if (column === 'totalGpsInserts') return Number(item.totalGpsInserts || 0);
      if (column === 'avgGpsInserts') return Number(item.avgGpsInserts || 0);
      return 0;
    };
    items.sort((a, b) => {
      if (sortColumn === 'domain' || sortColumn === 'adminPortal') {
        const compare = a.domain.localeCompare(b.domain);
        return sortDirection === 'asc' ? compare : -compare;
      }
      if (sortColumn === 'coTeacherAccessLevel') {
        const rank = { off: 0, basic: 1, premium: 2 } as const;
        const compare = rank[getEffectiveCoTeacherAccess(a.domain)] - rank[getEffectiveCoTeacherAccess(b.domain)];
        return sortDirection === 'asc' ? compare : -compare;
      }
      if (sortColumn === 'dashboardMaxRecords') {
        const compare = getEffectiveMaxRecords(a.domain) - getEffectiveMaxRecords(b.domain);
        return sortDirection === 'asc' ? compare : -compare;
      }
      if (sortColumn === 'feedbackRequestEmailsEnabled') {
        const aLabel = getEffectiveFeedbackEmailsEnabled(a.domain) ? 'On' : 'Off';
        const bLabel = getEffectiveFeedbackEmailsEnabled(b.domain) ? 'On' : 'Off';
        const compare = aLabel.localeCompare(bLabel);
        return sortDirection === 'asc' ? compare : -compare;
      }
      if (sortColumn === 'naOptionAccessEnabled') {
        const aLabel = getEffectiveNaOptionAccess(a.domain) ? 'On' : 'Off';
        const bLabel = getEffectiveNaOptionAccess(b.domain) ? 'On' : 'Off';
        const compare = aLabel.localeCompare(bLabel);
        return sortDirection === 'asc' ? compare : -compare;
      }
      if (sortColumn === 'selectionReminderAccessEnabled') {
        const aLabel = getEffectiveReminderAccess(a.domain) ? 'On' : 'Off';
        const bLabel = getEffectiveReminderAccess(b.domain) ? 'On' : 'Off';
        const compare = aLabel.localeCompare(bLabel);
        return sortDirection === 'asc' ? compare : -compare;
      }
      if (sortColumn === 'lastClickAt') {
        const aTime = a.lastClickAt ? new Date(a.lastClickAt).getTime() : 0;
        const bTime = b.lastClickAt ? new Date(b.lastClickAt).getTime() : 0;
        const compare = (Number.isFinite(aTime) ? aTime : 0) - (Number.isFinite(bTime) ? bTime : 0);
        return sortDirection === 'asc' ? compare : -compare;
      }
      const compare = numericValue(a, sortColumn) - numericValue(b, sortColumn);
      return sortDirection === 'asc' ? compare : -compare;
    });
    return items;
  }, [filteredDomains, sortColumn, sortDirection, coTeacherOverrides, maxRecordOverrides, feedbackEmailOverrides, naOptionOverrides, reminderOverrides, domains]);

  const handleCoTeacherAccessChange = (domain: string, nextValue: 'off' | 'basic' | 'premium') => {
    const existing = domains.find((item) => item.domain === domain);
    if (!existing) return;
    const normalizedInitial = getEffectiveCoTeacherAccess(domain);
    if (normalizedInitial === nextValue) {
      setCoTeacherOverrides((prev) => {
        const next = { ...prev };
        delete next[domain];
        return next;
      });
      return;
    }
    setCoTeacherOverrides((prev) => ({ ...prev, [domain]: nextValue }));
  };

  const handleMaxRecordsChange = (domain: string, nextValue: number) => {
    const existing = domains.find((item) => item.domain === domain);
    if (!existing) return;
    const initialValue = getStoredMaxRecords(domain);
    if (initialValue === nextValue) {
      setMaxRecordOverrides((prev) => {
        const next = { ...prev };
        delete next[domain];
        return next;
      });
      return;
    }
    setMaxRecordOverrides((prev) => ({ ...prev, [domain]: nextValue }));
  };

  const handleFeedbackEmailsChange = (domain: string, nextValue: boolean) => {
    const existing = domains.find((item) => item.domain === domain);
    if (!existing) return;
    const initialValue = existing.feedbackRequestEmailsEnabled !== false;
    if (initialValue === nextValue) {
      setFeedbackEmailOverrides((prev) => {
        const next = { ...prev };
        delete next[domain];
        return next;
      });
      return;
    }
    setFeedbackEmailOverrides((prev) => ({ ...prev, [domain]: nextValue }));
  };

  const handleNaOptionAccessChange = (domain: string, nextValue: boolean) => {
    const existing = domains.find((item) => item.domain === domain);
    if (!existing) return;
    const initialValue = existing.naOptionAccessEnabled === true;
    if (initialValue === nextValue) {
      setNaOptionOverrides((prev) => {
        const next = { ...prev };
        delete next[domain];
        return next;
      });
      return;
    }
    setNaOptionOverrides((prev) => ({ ...prev, [domain]: nextValue }));
  };

  const handleReminderAccessChange = (domain: string, nextValue: boolean) => {
    const existing = domains.find((item) => item.domain === domain);
    if (!existing) return;
    const initialValue = existing.selectionReminderAccessEnabled === true;
    if (initialValue === nextValue) {
      setReminderOverrides((prev) => {
        const next = { ...prev };
        delete next[domain];
        return next;
      });
      return;
    }
    setReminderOverrides((prev) => ({ ...prev, [domain]: nextValue }));
  };

  const hasChanges = Object.keys(coTeacherOverrides).length > 0
    || Object.keys(maxRecordOverrides).length > 0
    || Object.keys(feedbackEmailOverrides).length > 0
    || Object.keys(naOptionOverrides).length > 0
    || Object.keys(reminderOverrides).length > 0;

  const handleSaveAll = async () => {
    if (!hasChanges || saving) return;
    setSaving(true);
    setError(null);
    try {
      const token = getEffectiveToken();
      if (!token) {
        setError('Not authenticated');
        setSaving(false);
        return;
      }
      const baseUrl = API_BASE_URL_WITH_API.endsWith('/api')
        ? API_BASE_URL_WITH_API
        : `${API_BASE_URL_WITH_API}/api`;

      const domainsToSave = new Set([
        ...Object.keys(coTeacherOverrides),
        ...Object.keys(maxRecordOverrides),
        ...Object.keys(feedbackEmailOverrides),
        ...Object.keys(naOptionOverrides),
        ...Object.keys(reminderOverrides)
      ]);
      for (const domain of domainsToSave) {
        const patchPayload: {
          coTeacherAccessLevel?: 'off' | 'basic' | 'premium';
          dashboardMaxRecords?: number;
          feedbackRequestEmailsEnabled?: boolean;
          naOptionAccessEnabled?: boolean;
          selectionReminderAccessEnabled?: boolean;
        } = {};
        if (coTeacherOverrides[domain] !== undefined) {
          patchPayload.coTeacherAccessLevel = coTeacherOverrides[domain];
        }
        if (maxRecordOverrides[domain] !== undefined) {
          patchPayload.dashboardMaxRecords = maxRecordOverrides[domain];
        }
        if (feedbackEmailOverrides[domain] !== undefined) {
          patchPayload.feedbackRequestEmailsEnabled = feedbackEmailOverrides[domain];
        }
        if (naOptionOverrides[domain] !== undefined) {
          patchPayload.naOptionAccessEnabled = naOptionOverrides[domain];
        }
        if (reminderOverrides[domain] !== undefined) {
          patchPayload.selectionReminderAccessEnabled = reminderOverrides[domain];
        }
        const response = await fetch(`${baseUrl}/admin/domains/${encodeURIComponent(domain)}`, {
          method: 'PATCH',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(patchPayload)
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(data.error || `Failed to save domain settings for ${domain}`);
        }
      }
      await fetchDomains();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to save domain settings';
      setError(message);
    } finally {
      setSaving(false);
    }
  };

  const escapeCsvValue = (value: string) => `"${(value || '').replace(/"/g, '""')}"`;

  const handleExportCsv = () => {
    const headers = [
      'Domain',
      'Number of Teachers',
      'Number of Admins',
      'Admin Portal',
      'Max Records',
      'Feedback Emails',
      'GPS Access',
      'N/A',
      'Reminder',
      'Number of Groups',
      'Total Weekly Clicks',
      'Avg Weekly Clicks',
      'Total GPS Clicks',
      'Avg GPS Clicks',
      'Total GPS Inserts',
      'Avg GPS Inserts',
      'Last Click'
    ];
    const rows = sortedDomains.map((item) => [
      item.domain,
      String(item.teacherCount),
      String(item.adminCount),
      item.adminCount > 0 ? 'Sign in as Admin' : 'No Admin Available',
      String(getEffectiveMaxRecords(item.domain)),
      getEffectiveFeedbackEmailsEnabled(item.domain) ? 'On' : 'Off',
      getEffectiveCoTeacherAccess(item.domain),
      getEffectiveNaOptionAccess(item.domain) ? 'On' : 'Off',
      getEffectiveReminderAccess(item.domain) ? 'On' : 'Off',
      String(item.groupCount || 0),
      String(item.totalWeeklyClicks || 0),
      formatAverage(item.avgWeeklyClicks),
      String(item.totalGpsClicks || 0),
      formatAverage(item.avgGpsClicks),
      String(item.totalGpsInserts || 0),
      formatAverage(item.avgGpsInserts),
      formatLastClick(item.lastClickAt)
    ]);

    const csv = [
      headers.map(escapeCsvValue).join(','),
      ...rows.map((row) => row.map((cell) => escapeCsvValue(String(cell ?? ''))).join(','))
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    const timestamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
    link.href = url;
    link.download = `traffic-lite-domains-${timestamp}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleSignOut = () => {
    if (!window.confirm('Are you sure you want to sign out?')) {
      return;
    }

    clearAllAuthStorage();

    const runtime = getChromeRuntime();
    if (runtime) {
      const message = { action: 'CLEAR_ALL_DATA' };
      const extensionId = getExtensionId();
      try {
        if (extensionId) {
          runtime.sendMessage(extensionId, message, () => {});
        } else {
          runtime.sendMessage(message, () => {});
        }
      } catch (sendError) {
        console.error('Sign-out storage clear failed:', sendError);
      }
    }

    window.location.href = `${window.location.origin}${window.location.pathname}#/signout-confirmation`;
  };

  const handleStartImpersonation = async (domain: string) => {
    setError(null);
    try {
      const token = getEffectiveToken();
      if (!token) {
        setError('Not authenticated. Please sign in again.');
        return;
      }
      const baseUrl = API_BASE_URL_WITH_API.endsWith('/api')
        ? API_BASE_URL_WITH_API
        : `${API_BASE_URL_WITH_API}/api`;
      const response = await fetch(`${baseUrl}/admin/impersonation/start`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ domain })
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || 'Failed to start impersonation');
      }

      const payload = window.btoa(JSON.stringify({
        token: data.token,
        user: data.user,
        domain: data.domain,
        targetEmail: data.targetEmail,
        redirectPath: data.redirectPath,
        returnPath: data.returnPath,
        returnLabel: data.returnLabel,
        actorRole: data.actorRole
      }));
      const url = `${window.location.origin}${window.location.pathname}#/admin/impersonate?payload=${encodeURIComponent(payload)}`;
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to start impersonation');
    }
  };

  const sidebarItems: SideMenuItem[] = [
    { key: 'users', label: 'Users', onClick: () => navigate('/admin/users'), icon: MENU_ICON_PATHS.users },
    { key: 'domains', label: 'Domains', onClick: () => navigate('/admin/domains'), isActive: true, icon: MENU_ICON_PATHS.domains },
    { key: 'dashboard', label: 'Data Dashboard', onClick: () => navigate('/admin'), icon: MENU_ICON_PATHS.dataDashboard },
    { key: 'policy', label: 'Policy Templates', onClick: () => navigate('/admin/settings'), icon: MENU_ICON_PATHS.policyDetails },
    { key: 'aiAssist', label: 'GPS', onClick: () => navigate('/admin/ai-assist'), icon: MENU_ICON_PATHS.aiAssist },
    { key: 'feedback', label: 'Feedback', onClick: () => navigate('/admin/feedback'), icon: MENU_ICON_PATHS.feedback },
    { key: 'signout', label: 'Sign Out', onClick: handleSignOut, isDanger: true, icon: MENU_ICON_PATHS.signOut }
  ];

  const renderHeader = (column: SortColumn, label: string, width?: string, extraStyle?: React.CSSProperties) => (
    <th
      onClick={() => handleSort(column)}
      style={{ ...compactHeaderStyle, width, ...extraStyle }}
    >
      {label}{renderSortIndicator(column)}
    </th>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-6">
      <div className={usePersistentSidebar ? 'max-w-[1600px] mx-auto flex gap-6 items-start' : 'max-w-[1400px] mx-auto'}>
        {usePersistentSidebar && <SideMenuCard title="Super Admin Menu" items={sidebarItems} />}
        <div className={`${usePersistentSidebar ? 'flex-1 min-w-0' : ''} bg-white rounded-lg shadow-md p-8`} style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
          <AdminHeader
            title="Traffic Lite Super Admin"
            subtitle="Domains"
            helperText="Use the table below to manage domain-level GPS and Enforce access plus dashboard record caps."
            menuOpen={menuOpen}
            setMenuOpen={setMenuOpen}
          />
          <ImpersonationBanner />

          {!viewerIsSuperAdmin && (
            <div className="text-center py-10 text-gray-500">Checking permissions...</div>
          )}

          {error && (
            <div
              className="mb-6 p-4 rounded-lg"
              style={{
                backgroundColor: '#fee',
                border: '1px solid #fcc',
                color: '#c00'
              }}
            >
              {error}
            </div>
          )}

          {loading ? (
            <div className="text-center py-16 text-gray-500">Loading domains...</div>
          ) : (
            <div>
              <div className="mb-4 flex items-center justify-between flex-wrap gap-4">
                <p className="text-gray-600">
                  {sortedDomains.length} domain{sortedDomains.length !== 1 ? 's' : ''} with Traffic Lite installed users
                </p>
                <div className="flex items-center gap-2">
                  <div>
                    <select
                      value={selectedDomainFilter}
                      onChange={(e) => setSelectedDomainFilter(e.target.value)}
                      style={{
                        width: '100%',
                        minWidth: '220px',
                        padding: '8px 12px',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        fontSize: '14px',
                        backgroundColor: 'white'
                      }}
                    >
                      <option value="">All domains</option>
                      {domainFilterOptions.map((domain) => (
                        <option key={domain} value={domain}>
                          {domain}
                        </option>
                      ))}
                    </select>
                  </div>
                  <button
                    type="button"
                    onClick={handleExportCsv}
                    style={{
                      padding: '8px 20px',
                      fontSize: '14px',
                      fontWeight: 600,
                      backgroundColor: '#455a64',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer'
                    }}
                  >
                    Export CSV
                  </button>
                  <button
                    type="button"
                    onClick={handleSaveAll}
                    disabled={!hasChanges || saving}
                    style={{
                      padding: '8px 20px',
                      fontSize: '14px',
                      fontWeight: 600,
                      backgroundColor: hasChanges && !saving ? '#4285f4' : '#9e9e9e',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: hasChanges && !saving ? 'pointer' : 'not-allowed'
                    }}
                  >
                    {saving ? 'Saving...' : 'Save'}
                  </button>
                </div>
              </div>
              <div style={{ overflowX: 'auto' }}>
                <table
                  className="portal-table"
                  style={{
                    width: '100%',
                    borderCollapse: 'collapse',
                    fontSize: '13px',
                    tableLayout: 'auto'
                  }}
                >
                  <thead>
                    <tr
                      style={{
                        backgroundColor: '#212529',
                        borderBottom: '2px solid #212529'
                      }}
                    >
                      {renderHeader('domain', 'Domain', '180px')}
                      {renderHeader('teacherCount', 'Number of Teachers', '78px')}
                      {renderHeader('adminCount', 'Number of Admins', '72px')}
                      {renderHeader('adminPortal', 'Log in as User', '58px')}
                      {renderHeader('dashboardMaxRecords', 'Max Records', '156px')}
                      {renderHeader('feedbackRequestEmailsEnabled', 'Feedback Emails?', '96px')}
                      {renderHeader('coTeacherAccessLevel', 'GPS', '86px')}
                      {renderHeader('naOptionAccessEnabled', 'N/A', '78px')}
                      {renderHeader('selectionReminderAccessEnabled', 'Reminder', '88px')}
                      {renderHeader('groupCount', 'Number of Groups', '72px')}
                      {renderHeader('totalWeeklyClicks', 'Total Weekly Clicks', '78px')}
                      {renderHeader('avgWeeklyClicks', 'Avg Weekly Clicks', '72px')}
                      {renderHeader('totalGpsClicks', 'Total GPS Clicks', '72px')}
                      {renderHeader('avgGpsClicks', 'Avg GPS Clicks', '68px')}
                      {renderHeader('totalGpsInserts', 'Total GPS Inserts', '74px')}
                      {renderHeader('avgGpsInserts', 'Avg GPS Inserts', '70px')}
                      {renderHeader('lastClickAt', 'Last Click', '108px', { borderRight: 'none' })}
                    </tr>
                  </thead>
                  <tbody>
                    {sortedDomains.map((item, index) => {
                      const gpsLevel = getEffectiveCoTeacherAccess(item.domain);
                      const maxRecords = getEffectiveMaxRecords(item.domain);
                      const feedbackEmailsEnabled = getEffectiveFeedbackEmailsEnabled(item.domain);
                      const naOptionAccessEnabled = getEffectiveNaOptionAccess(item.domain);
                      const reminderAccessEnabled = getEffectiveReminderAccess(item.domain);
                      return (
                        <tr
                          key={item.domain}
                          style={{
                            borderBottom: '1px solid #eee',
                            backgroundColor: index % 2 === 0 ? 'white' : '#fafafa'
                          }}
                        >
                          <td style={{ ...compactCellStyle, textAlign: 'left' }}>{item.domain}</td>
                          <td style={compactCellStyle}>{item.teacherCount}</td>
                          <td style={compactCellStyle}>{item.adminCount}</td>
                          <td style={compactCellStyle}>
                            <button
                              type="button"
                              onClick={() => void handleStartImpersonation(item.domain)}
                              disabled={item.adminCount < 1}
                              title="View Admin Portal"
                              aria-label="View Admin Portal"
                              className="icon-action-btn"
                            >
                              <span
                                aria-hidden="true"
                                className="icon-action-glyph"
                                style={{
                                  opacity: item.adminCount > 0 ? 1 : 0.45,
                                  WebkitMaskImage: `url(${MENU_ICON_PATHS.loginAsUser})`,
                                  maskImage: `url(${MENU_ICON_PATHS.loginAsUser})`
                                }}
                              />
                            </button>
                          </td>
                          <td style={compactCellStyle}>
                            <select
                              value={maxRecords}
                              onChange={(e) => handleMaxRecordsChange(item.domain, Number(e.target.value))}
                              aria-label={`Set max records for ${item.domain}`}
                              style={{
                                width: '148px',
                                padding: '4px 4px',
                                border: '1px solid',
                                borderRadius: '6px',
                                fontSize: '12px',
                                fontWeight: 500,
                                ...getMaxRecordsBadgeStyles(maxRecords)
                              }}
                            >
                              {maxRecordChoicesFor(item.domain).map((choice) => (
                                <option key={choice.value} value={choice.value}>
                                  {choice.label}
                                </option>
                              ))}
                            </select>
                          </td>
                          <td style={compactCellStyle}>
                            <select
                              value={feedbackEmailsEnabled ? 'on' : 'off'}
                              onChange={(e) => handleFeedbackEmailsChange(item.domain, e.target.value === 'on')}
                              aria-label={`Set feedback emails for ${item.domain}`}
                              style={{
                                width: '78px',
                                padding: '4px 4px',
                                border: '1px solid',
                                borderRadius: '6px',
                                fontSize: '12px',
                                fontWeight: 500,
                                ...getOnOffBadgeStyles(feedbackEmailsEnabled)
                              }}
                            >
                              <option value="on">On</option>
                              <option value="off">Off</option>
                            </select>
                          </td>
                          <td style={compactCellStyle}>
                            <select
                              value={gpsLevel}
                              onChange={(e) => handleCoTeacherAccessChange(item.domain, e.target.value as 'off' | 'basic' | 'premium')}
                              aria-label={`Set GPS access for ${item.domain}`}
                              style={{
                                width: '78px',
                                padding: '4px 4px',
                                border: '1px solid',
                                borderRadius: '6px',
                                fontSize: '12px',
                                fontWeight: 500,
                                ...getGpsBadgeStyles(gpsLevel)
                              }}
                            >
                              <option value="off">Off</option>
                              <option value="basic">Basic</option>
                              <option value="premium">Premium</option>
                            </select>
                          </td>
                          <td style={compactCellStyle}>
                            <select
                              value={naOptionAccessEnabled ? 'on' : 'off'}
                              onChange={(e) => handleNaOptionAccessChange(item.domain, e.target.value === 'on')}
                              aria-label={`Set N/A access for ${item.domain}`}
                              style={{
                                width: '70px',
                                padding: '4px 4px',
                                border: '1px solid',
                                borderRadius: '6px',
                                fontSize: '12px',
                                fontWeight: 500,
                                ...getOnOffBadgeStyles(naOptionAccessEnabled)
                              }}
                            >
                              <option value="on">On</option>
                              <option value="off">Off</option>
                            </select>
                          </td>
                          <td style={compactCellStyle}>
                            <select
                              value={reminderAccessEnabled ? 'on' : 'off'}
                              onChange={(e) => handleReminderAccessChange(item.domain, e.target.value === 'on')}
                              aria-label={`Set reminder access for ${item.domain}`}
                              style={{
                                width: '70px',
                                padding: '4px 4px',
                                border: '1px solid',
                                borderRadius: '6px',
                                fontSize: '12px',
                                fontWeight: 500,
                                ...getOnOffBadgeStyles(reminderAccessEnabled)
                              }}
                            >
                              <option value="on">On</option>
                              <option value="off">Off</option>
                            </select>
                          </td>
                          <td style={compactCellStyle}>{item.groupCount || 0}</td>
                          <td style={compactCellStyle}>{item.totalWeeklyClicks || 0}</td>
                          <td style={compactCellStyle}>{formatAverage(item.avgWeeklyClicks)}</td>
                          <td style={compactCellStyle}>{item.totalGpsClicks || 0}</td>
                          <td style={compactCellStyle}>{formatAverage(item.avgGpsClicks)}</td>
                          <td style={compactCellStyle}>{item.totalGpsInserts || 0}</td>
                          <td style={compactCellStyle}>{formatAverage(item.avgGpsInserts)}</td>
                          <td style={{ ...compactCellStyle, borderRight: 'none' }}>{formatLastClick(item.lastClickAt)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {sortedDomains.length === 0 && !error && (
                <div className="text-center py-12 text-gray-500">No domains found.</div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminDomainsPage;
